import { Key, Truck } from 'lucide-react'
import { useForm } from 'react-hook-form'

import { useEffect } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

import { TextInput, SelectInput, SwitchInput } from '@/components/atoms'

import { DeliveryMethodModel } from '@/types'

import { DeliveryMethodFormState, deliveryMethodFieldsConfig } from './deliveryMethodFieldsConfig'
import { useEnterSubmit } from '@/hooks/ui'
import { Spinner } from '@/components/ui/spinner'

interface DeliveryMethodDialogProps {
  open: boolean
  mode: 'create' | 'edit' | 'view'
  editMethod: DeliveryMethodModel.DeliveryMethod | null
  closeDialog: () => void
  onCreate: (data: DeliveryMethodFormState) => void
  onUpdate: (key: string, data: DeliveryMethodFormState) => void
  loading?: boolean
}

export const DeliveryMethodDialog = ({
  open,
  mode,
  editMethod,
  closeDialog,
  onCreate,
  onUpdate,
  loading = false,
}: DeliveryMethodDialogProps) => {
  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
    control,
  } = useForm<DeliveryMethodFormState>({
    defaultValues: {
      key: '',
      name: '',
      type: 'manual',
      charge: 0,
      credential_type: 'sandbox',
      status: 'inactive',
      credentials: {},
    },
  })

  const formValues = watch()
  const { fields } = deliveryMethodFieldsConfig({ formValues })

  useEffect(() => {
    if ((mode === 'edit' || mode === 'view') && editMethod) {
      reset({
        key: editMethod.key,
        name: editMethod.name,
        type: editMethod.type,
        charge: editMethod.charge,
        credential_type: editMethod.credential_type,
        status: editMethod.status,
        credentials: editMethod.credentials || {},
      })
    } else {
      reset({
        key: '',
        name: '',
        type: 'manual',
        charge: 0,
        credential_type: 'sandbox',
        status: 'inactive',
        credentials: {},
      })
    }
  }, [mode, editMethod, reset])

  const onSubmit = (data: DeliveryMethodFormState) => {
    if (mode === 'create') {
      onCreate(data)
    } else {
      if (!editMethod?.key) return
      onUpdate(editMethod.key, data)
    }
  }

  useEnterSubmit(handleSubmit(onSubmit), open && mode !== 'view')

  const credentialsSchema = editMethod?.credentials_schema || {}
  const EXCLUDED_KEYS = ['access_token', 'refresh_token', 'expires_at', 'token_type']
  const schemaKeys = (
    Object.keys(credentialsSchema).length > 0
      ? Object.keys(credentialsSchema)
      : Object.keys(editMethod?.credentials || {})
  ).filter((key) => !EXCLUDED_KEYS.includes(key))

  return (
    <Dialog open={open} onOpenChange={closeDialog}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create'
              ? 'Add delivery method'
              : mode === 'edit'
                ? `Configure ${editMethod?.name}`
                : 'Delivery method details'}
          </DialogTitle>
          <DialogDescription>
            {editMethod?.type === 'courier'
              ? 'Set the API credentials and delivery charge for this courier.'
              : 'Set up manual delivery options for your store.'}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="general" className="w-full">
          <TabsList>
            <TabsTrigger value="general">Details</TabsTrigger>
            <TabsTrigger
              value="credentials"
              disabled={mode === 'create' && formValues.type === 'manual'}
            >
              Credentials
            </TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="mt-0 space-y-4 pt-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <TextInput
                control={control}
                name="name"
                label="Name"
                placeholder="e.g. Steadfast Courier"
                disabled={mode === 'view' || (mode === 'edit' && editMethod?.type === 'courier')}
                rules={fields.name.rules}
                errors={errors}
              />
              <TextInput
                control={control}
                name="key"
                label="Key"
                placeholder="e.g. steadfast"
                disabled={mode !== 'create'}
                rules={fields.key.rules}
                errors={errors}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <TextInput
                control={control}
                name="charge"
                label="Delivery charge"
                placeholder="80"
                type="number"
                disabled={mode === 'view'}
                rules={fields.charge.rules}
                min={fields.charge.min}
                errors={errors}
              />
            </div>

            <div className="border-t border-border-subtle pt-4">
              <SwitchInput
              control={control}
              name="status"
                label="Active"
                description="Offer this delivery method at checkout."
                disabled={mode === 'view'}
                activeValue="active"
                inactiveValue="inactive"
              />
            </div>
          </TabsContent>

          <TabsContent value="credentials" className="mt-0 space-y-4 pt-4">
            {editMethod?.type === 'courier' ? (
              <>
                <div className="flex gap-2 rounded-lg bg-subdued p-3">
                  <Key className="mt-0.5 size-4 shrink-0 text-text-2" />
                  <div className="space-y-0.5">
                    <p className="text-sm font-medium text-ink">API credentials</p>
                    <p className="text-sm text-text-2">
                      Enter the credentials from your {editMethod.name} dashboard.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <SelectInput
                    control={control}
                    name="credential_type"
                    label="Environment"
                    disabled={mode === 'view'}
                    options={[
                      { label: 'Sandbox (testing)', value: 'sandbox' },
                      { label: 'Production', value: 'production' },
                    ]}
                  />
                </div>

                <div className="space-y-4 border-t border-border-subtle pt-4">
                  {schemaKeys.length > 0 ? (
                    schemaKeys.map((key) => (
                      <div key={key}>
                        <TextInput
                          label={key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())}
                          control={control}
                          name={`credentials.${key}` as any}
                          placeholder={`Enter ${key.replace(/_/g, ' ')}`}
                          disabled={mode === 'view'}
                          type={
                            key.includes('secret') ||
                              key.includes('password') ||
                              key.includes('key')
                              ? 'password'
                              : 'text'
                          }
                          rules={{
                            required:
                              credentialsSchema[key] === 'required'
                                ? `${key} is required`
                                : false,
                          }}
                          errors={errors}
                        />
                      </div>
                    ))
                  ) : (
                    <p className="py-8 text-center text-sm text-text-2">
                      No specific credentials required for this integration.
                    </p>
                  )}
                </div>
              </>
            ) : (
              <div className="py-12 text-center">
                <Truck className="mx-auto mb-3 size-8 text-muted-foreground" />
                <h3 className="text-heading-md text-ink">Manual delivery</h3>
                <p className="text-sm text-text-2">
                  This method does not require API credentials.
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={closeDialog} disabled={loading}>
            {mode === 'view' ? 'Close' : 'Cancel'}
          </Button>
          {mode !== 'view' && (
            <Button type="button" disabled={loading} onClick={handleSubmit(onSubmit)}>
              {loading && <Spinner />}
              {mode === 'create' ? 'Add method' : 'Save'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
